import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle, ListChecks, X } from 'lucide-react';
import { VENDOR_PAGE_GUIDES } from '../../../utils/vendorPageGuides';

/**
 * Consistent page title + optional primary action for vendor screens.
 * Refresh lives in VendorHeader (top bar) so every vendor page can reload data.
 */
export function VendorPageHeader({
 title,
 subtitle,
 actions = null,
 className = '',
}) {
 return (
 <div
 className={`mb-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between ${className}`}
 >
 <div className="min-w-0">
 <h1 className="mb-0.5 text-lg font-semibold tracking-tight text-ink">
 {title}
 </h1>
 {subtitle ? (
 <p className="max-w-2xl text-sm leading-relaxed text-ink-secondary">
 {subtitle}
 </p>
 ) : null}
 </div>
 {actions ? (
 <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
 {actions}
 </div>
 ) : null}
 </div>
 );
}

/**
 * Uniform outer shell for vendor pages rendered inside VendorLayout.
 * Layout already provides page padding — do not add another outer p-* here.
 */
export function VendorPageShell({
 title,
 subtitle,
 actions = null,
 guideKey = null,
 guideTone = 'sky',
 children,
 className = '',
 contentClassName = 'space-y-5',
}) {
 const guide = guideKey ? VENDOR_PAGE_GUIDES[guideKey] : null;

 return (
 <div className={`${contentClassName} ${className}`}>
 <VendorPageHeader title={title} subtitle={subtitle} actions={actions} />
 {guide ? (
 <VendorGuideCallout
 title={guide.title}
 steps={guide.steps}
 tone={guideTone}
 icon={ListChecks}
 />
 ) : null}
 {children}
 </div>
 );
}

/**
 * Soft how-to / tip strip under the page header.
 */
export function VendorGuideCallout({
 title,
 children,
 steps = [],
 tone = 'slate',
 icon: Icon = ListChecks,
 action = null,
 className = '',
}) {
 const tones = {
 sky: {
 wrap: 'border-border bg-surface-muted',
 iconWrap: 'bg-surface border-border text-ink',
 title: 'text-ink',
 body: 'text-ink-secondary',
 step: 'text-ink-tertiary',
 },
 amber: {
 wrap: 'border-amber-200/80 bg-amber-50/80',
 iconWrap: 'bg-surface border-amber-100 text-amber-700',
 title: 'text-amber-950',
 body: 'text-amber-900/80',
 step: 'text-amber-700',
 },
 emerald: {
 wrap: 'border-emerald-200/80 bg-emerald-50/80',
 iconWrap: 'bg-surface border-emerald-100 text-emerald-700',
 title: 'text-emerald-950',
 body: 'text-emerald-900/80',
 step: 'text-emerald-700',
 },
 slate: {
 wrap: 'border-gray-200 bg-surface',
 iconWrap: 'bg-surface-muted border-gray-200 text-gray-700',
 title: 'text-ink',
 body: 'text-ink-secondary',
 step: 'text-ink-tertiary',
 },
 };
 const t = tones[tone] || tones.slate;

 return (
 <div className={`rounded-product-md border px-4 py-3.5 sm:px-5 sm:py-4 ${t.wrap} ${className}`}>
 <div className="flex items-start gap-3">
 <div
 className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${t.iconWrap}`}
 >
 <Icon className="w-3.5 h-3.5" aria-hidden />
 </div>
 <div className="min-w-0 flex-1">
 {title ? <h2 className={`text-sm font-semibold tracking-tight ${t.title}`}>{title}</h2> : null}
 {children ? <div className={`mt-1 text-sm leading-relaxed ${t.body}`}>{children}</div> : null}
 {steps.length > 0 ? (
 <ol className={`mt-2.5 space-y-1 text-sm leading-snug ${t.title}`}>
 {steps.map((step, i) => (
 <li key={i} className="flex gap-2">
 <span className={`font-semibold shrink-0 tabular-nums ${t.step}`}>{i + 1}.</span>
 <span className={t.body}>{step}</span>
 </li>
 ))}
 </ol>
 ) : null}
 {action ? <div className="mt-3">{action}</div> : null}
 </div>
 </div>
 </div>
 );
}

/**
 * Friendly empty state with optional CTA.
 */
export function VendorEmptyState({
 icon: Icon,
 title,
 description,
 actionLabel,
 actionTo,
 onAction,
 tips = [],
 className = '',
}) {
 return (
 <div
 className={`bg-surface border border-dashed border-gray-300 rounded-xl p-8 sm:p-10 text-center ${className}`}
 >
 {Icon ? <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" aria-hidden /> : null}
 <p className="text-gray-900 font-semibold mb-1">{title}</p>
 {description ? <p className="text-sm text-gray-500 max-w-md mx-auto mb-4">{description}</p> : null}
 {tips.length > 0 ? (
 <ul className="text-left max-w-sm mx-auto mb-5 space-y-1.5 text-sm text-gray-600">
 {tips.map((tip, i) => (
 <li key={i} className="flex gap-2">
 <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
 <span>{tip}</span>
 </li>
 ))}
 </ul>
 ) : null}
 {actionLabel && (actionTo || onAction) ? (
 actionTo ? (
 <Link
 to={actionTo}
 className="inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-lg bg-ink text-white hover:bg-black"
 >
 {actionLabel}
 <ArrowRight className="w-4 h-4" />
 </Link>
 ) : (
 <button
 type="button"
 onClick={onAction}
 className="inline-flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium rounded-lg bg-ink text-white hover:bg-black"
 >
 {actionLabel}
 <ArrowRight className="w-4 h-4" />
 </button>
 )
 ) : null}
 </div>
 );
}

/**
 * Start-to-finish setup checklist for the vendor dashboard.
 */
export function VendorGettingStarted({
 steps = [],
 onDismiss,
 className = '',
}) {
 const doneCount = steps.filter((s) => s.done).length;
 const total = steps.length || 1;
 const pct = Math.round((doneCount / total) * 100);
 const allDone = doneCount === steps.length && steps.length > 0;

 if (!steps.length) return null;

 return (
 <div
 className={`rounded-lg border border-gray-200/80 bg-surface shadow-sm overflow-hidden ${className}`}
 >
 <div className="flex items-start justify-between gap-3 px-5 pt-5">
 <div>
 <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 mb-1">
 Getting started
 </p>
 <h2 className="text-lg font-bold text-gray-900 tracking-tight">
 Set up your vendor account
 </h2>
 <p className="mt-1 text-sm text-gray-600 leading-relaxed">
 Follow these steps so clients can find you, book you, and pay you.
 </p>
 </div>
 {onDismiss ? (
 <button
 type="button"
 onClick={onDismiss}
 className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
 aria-label="Dismiss getting started"
 >
 <X className="w-4 h-4" />
 </button>
 ) : null}
 </div>

 <div className="px-5 mt-4">
 <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
 <span>
 {doneCount} of {steps.length} complete
 </span>
 <span className="tabular-nums">{pct}%</span>
 </div>
 <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
 <div
 className="h-full rounded-full bg-gray-900 transition-all duration-500"
 style={{ width: `${pct}%` }}
 />
 </div>
 </div>

 <ol className="mt-4 divide-y divide-gray-100">
 {steps.map((step) => {
 const StepIcon = step.done ? CheckCircle2 : Circle;
 return (
 <li key={step.id} className="px-5 py-3.5 flex items-start gap-3">
 <StepIcon
 className={`w-5 h-5 shrink-0 mt-0.5 ${ step.done ? 'text-emerald-600' : 'text-gray-300' }`}
 aria-hidden
 />
 <div className="min-w-0 flex-1">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
 <div>
 <p
 className={`text-sm font-semibold ${ step.done ? 'text-gray-500 line-through' : 'text-gray-900' }`}
 >
 {step.title}
 </p>
 <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{step.description}</p>
 </div>
 {!step.done && step.href ? (
 <Link
 to={step.href}
 className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-gray-900 hover:text-gray-700"
 >
 {step.cta || 'Continue'}
 <ArrowRight className="w-3.5 h-3.5" />
 </Link>
 ) : null}
 </div>
 </div>
 </li>
 );
 })}
 </ol>

 {allDone ? (
 <div className="px-5 py-3 bg-emerald-50 border-t border-emerald-100 text-sm text-emerald-800">
 You&apos;re set up. Keep an eye on Bookings and grow with Portfolio, Invoices, and Contracts.
 </div>
 ) : null}
 </div>
 );
}
